// src/components/ModalProProfileEdit.js
import React, {
  useContext,
  useState,
  useEffect,
  useRef,
  useCallback
} from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchProfessionalById } from '../api/queries';
import { AuthContext } from '../context/AuthContext';
import { ModalContext } from '../context/ModalContext';
import { ModalHeaderContext } from '../context/ModalHeaderContext';
import { TabContext } from '../context/TabContext';
import ModalImageHeader from './ModalImageHeader';
import ModalLocation from './ModalLocation';
import Spinner from './Spinner';
import API from '../api/client';
import { convertFileToJpeg } from '../utils/imageFormat';
import './ModalProProfileEdit.css';

export default function ModalProProfileEdit() {
  const { user } = useContext(AuthContext);
  const professionalId = user?.professional_id;
  const queryClient = useQueryClient();
  const { closeModal, openModal, modalStacks } = useContext(ModalContext);
  const { activeTab } = useContext(TabContext);
  const { registerHeaderActions } = useContext(ModalHeaderContext);

  // localizar el modal en la pila
  const currentStack = modalStacks[activeTab] || [];
  const proModal = currentStack
    .slice()
    .reverse()
    .find(m => m.metadata?.modalType === 'proProfileEdit');
  const modalId = proModal?.id;

  // ─── Estados para cabecera, logo, nombre y demás campos ────────────
  const [selectedFile, setSelectedFile]           = useState(null);
  const [previewUrl, setPreviewUrl]               = useState('');
  const [selectedLogoFile, setSelectedLogoFile]   = useState(null);
  const [previewLogoUrl, setPreviewLogoUrl]       = useState('');
  const [logoError, setLogoError]                 = useState(false);
  // Nombre
  const [nombre, setNombre]                       = useState('');
  const [initialNombre, setInitialNombre]         = useState('');
  // Tipo, Teléfono, WhatsApp, Email
  const [tipo, setTipo]                           = useState('');
  const [initialTipo, setInitialTipo]             = useState('');
  const [telefono, setTelefono]                   = useState('');
  const [initialTelefono, setInitialTelefono]     = useState('');
  const [whatsapp, setWhatsapp]                   = useState('');
  const [initialWhatsapp, setInitialWhatsapp]     = useState('');
  const [email, setEmail]                         = useState('');
  const [initialEmail, setInitialEmail]           = useState('');
  const [emailValid, setEmailValid]               = useState(true);
  const [emailMessage, setEmailMessage]           = useState('');
  // Horario (textarea auto-resize)
  const [horario, setHorario]                     = useState('');
  const [initialHorario, setInitialHorario]       = useState('');
  const horarioRef                                = useRef(null);

  // **Dirección + geolocalización**
  const [direccion, setDireccion]                 = useState('');
  const [location, setLocation]                   = useState({ address: '', lat: null, lng: null });
  const [initialLocation, setInitialLocation]     = useState({ address: '', lat: null, lng: null });

  // ─── Recorte de cabecera ───────────────────────────────────────────
  const [crop, setCrop]                           = useState({ x: 0, y: 0 });
  const [zoom, setZoom]                           = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [errorMsg, setErrorMsg]         = useState('');
  const [saving, setSaving]             = useState(false);

  // Sobre nosotros (aboutus) textarea
  const [aboutus, setAboutus] = useState('');
  const [initialAboutus, setInitialAboutus] = useState('');
  const aboutusRef = useRef(null);

  // ─── Cargar datos del profesional ──────────────────────────────────────
  const { data: profData, isLoading, isError } = useQuery({
    queryKey: ['professional', professionalId],
    queryFn: () => fetchProfessionalById(professionalId),
    enabled: !!professionalId
  });

  // ─── Cache para cabecera y logo ───────────────────────────────────────
  const { data: blobHeaderUrl } = useQuery({
    queryKey: ['profHeader', profData?.cabecera],
    queryFn: async () => {
      const res = await fetch(profData.cabecera);
      const blob = await res.blob();
      return URL.createObjectURL(blob);
    },
    enabled: !!profData?.cabecera,
    staleTime: 24 * 60 * 60_000,
    cacheTime: 24 * 60 * 60_000
  });
  const { data: blobLogoUrl } = useQuery({
    queryKey: ['profLogo', profData?.logo],
    queryFn: async () => {
      const res = await fetch(profData.logo);
      const blob = await res.blob();
      return URL.createObjectURL(blob);
    },
    enabled: !!profData?.logo,
    staleTime: 24 * 60 * 60_000,
    cacheTime: 24 * 60 * 60_000
  });

  // ─── Inicializar estados al cargar datos ─────────────────────────────
  useEffect(() => {
    if (!profData) return;

    // cabecera y logo
    setPreviewUrl(blobHeaderUrl || profData.cabecera);
    setPreviewLogoUrl(blobLogoUrl || profData.logo || '');
    setLogoError(false);

    // campos básicos
    setNombre(profData.nombre || '');
    setInitialNombre(profData.nombre || '');

    // Tipo
    setTipo(profData.tipo || '');
    setInitialTipo(profData.tipo || '');

    // Teléfono / WhatsApp
    const rawTel = profData.telefono || '';
    setTelefono(rawTel.replace(/^\+34/, ''));
    setInitialTelefono(rawTel.replace(/^\+34/, ''));

    const rawWsp = profData.whatsapp || '';
    setWhatsapp(rawWsp.replace(/^\+34/, ''));
    setInitialWhatsapp(rawWsp.replace(/^\+34/, ''));

    // Email
    setEmail(profData.email || '');
    setInitialEmail(profData.email || '');
    setEmailValid(true);
    setEmailMessage('');

    // Horario
    setHorario(profData.horario || '');
    setInitialHorario(profData.horario || '');

    // dirección + coordenadas
    setDireccion(profData.direccion || '');
    const geo = profData.ubicacion_geografica;
    setLocation({
      address: profData.direccion || '',
      lat: geo?.coordinates?.[1] ?? null,
      lng: geo?.coordinates?.[0] ?? null
    });
    setInitialLocation({
      address: profData.direccion || '',
      lat: geo?.coordinates?.[1] ?? null,
      lng: geo?.coordinates?.[0] ?? null
    });

    // ─── Campos Redes Sociales ────────────────────────────
    setEnlace(profData.enlace || '');
    setInitialEnlace(profData.enlace || '');
    setTwitter(profData.twitter || '');
    setInitialTwitter(profData.twitter || '');
    setFacebook(profData.facebook || '');
    setInitialFacebook(profData.facebook || '');
    setInstagram(profData.instagram || '');
    setInitialInstagram(profData.instagram || '');
    setYoutube(profData.youtube || '');
    setInitialYoutube(profData.youtube || '');

    // ─── Campos de colaboración ────────────────────────────
    setAcoge(profData.acoge || '');
    setInitialAcoge(profData.acoge || '');
    setApadrina(profData.apadrina || '');
    setInitialApadrina(profData.apadrina || '');
    setVoluntariado(profData.voluntariado || '');
    setInitialVoluntariado(profData.voluntariado || '');
    setDonaciones(profData.donaciones || '');
    setInitialDonaciones(profData.donaciones || '');
    setSocio(profData.socio || '');
    setInitialSocio(profData.socio || '');

    // Sobre nosotros
    setAboutus(profData.aboutus || '');
    setInitialAboutus(profData.aboutus || '');

    // Crop de cabecera
    if (profData.cabecera_crop) {
      const { crop: c, zoom: z, croppedAreaPixels: cap } = profData.cabecera_crop;
      setCrop(c); setZoom(z); setCroppedAreaPixels(cap);
    } else {
      setCrop({ x: 0, y: 0 }); setZoom(1); setCroppedAreaPixels(null);
    }
  }, [profData, blobHeaderUrl, blobLogoUrl]);

  // ─── Auto-resize del textarea de Horario ───────────────────────────────
  useEffect(() => {
    const el = horarioRef.current;
    if (!el) return;
    // reset height to auto to recalc
    el.style.height = 'auto';
    // then set to scrollHeight (CSS caps at max-height)
    el.style.height = `${el.scrollHeight}px`;
  }, [horario]);

  // ─── Auto-resize Aboutus ───────────────────────────────
  useEffect(() => {
    const el = aboutusRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [aboutus]);

  // ─── Adjust dinámico de la imagen de cabecera ──────────────────────────
  const containerRef = useRef(null);
  const imgRef       = useRef(null);
  const [naturalSize, setNaturalSize] = useState({ width: 0, height: 0 });
  const [imgStyle, setImgStyle]       = useState({
    position: 'absolute', width: '100%', height: '100%',
    left: '0', top: '0', objectFit: 'cover'
  });
  const handleImgLoad = () => {
    if (!imgRef.current) return;
    setNaturalSize({
      width: imgRef.current.naturalWidth,
      height: imgRef.current.naturalHeight
    });
  };
  useEffect(() => {
    if (!containerRef.current || !naturalSize.width) return;
    const cropRect = croppedAreaPixels || {
      x: 0, y: 0, width: naturalSize.width, height: naturalSize.height
    };
    const contW = containerRef.current.clientWidth;
    const scale = contW / cropRect.width;
    const scaledW = naturalSize.width * scale;
    const scaledH = naturalSize.height * scale;
    const left = scaledW < contW ? (contW - scaledW)/2 : -cropRect.x*scale;
    const top = scaledH < (contW*2)/5 ? ((contW*2)/5 - scaledH)/2 : -cropRect.y*scale;
    setImgStyle({
      position: 'absolute',
      width:  `${scaledW}px`,
      height: `${scaledH}px`,
      left:   `${left}px`,
      top:    `${top}px`,
      objectFit: 'cover'
    });
  }, [naturalSize, croppedAreaPixels]);

  // ─── **Estados Redes Sociales** ───────────────────────────────────
  const [enlace, setEnlace]             = useState('');
  const [initialEnlace, setInitialEnlace] = useState('');
  const [twitter, setTwitter]           = useState('');
  const [initialTwitter, setInitialTwitter] = useState('');
  const [facebook, setFacebook]         = useState('');
  const [initialFacebook, setInitialFacebook] = useState('');
  const [instagram, setInstagram]       = useState('');
  const [initialInstagram, setInitialInstagram] = useState('');
  const [youtube, setYoutube]           = useState('');
  const [initialYoutube, setInitialYoutube] = useState('');

  // ─── **Nuevos** estados para hábitos de colaboración ────────────────
  const [acoge, setAcoge]                       = useState('');
  const [initialAcoge, setInitialAcoge]         = useState('');
  const [apadrina, setApadrina]                 = useState('');
  const [initialApadrina, setInitialApadrina]   = useState('');
  const [voluntariado, setVoluntariado]         = useState('');
  const [initialVoluntariado, setInitialVoluntariado] = useState('');
  const [donaciones, setDonaciones]             = useState('');
  const [initialDonaciones, setInitialDonaciones] = useState('');
  const [socio, setSocio]                       = useState('');
  const [initialSocio, setInitialSocio]         = useState('');

  // ─── Estados para cambiar contraseña ───────────────────────
  const [isChangingPassword, setIsChangingPassword]       = useState(false);
  const [currentPassword, setCurrentPassword]             = useState('');
  const [password, setPassword]                           = useState('');
  const [password2, setPassword2]                         = useState('');
  const [lengthValid, setLengthValid]                     = useState(false);
  const [upperValid, setUpperValid]                       = useState(false);
  const [lowerValid, setLowerValid]                       = useState(false);
  const [numberValid, setNumberValid]                     = useState(false);
  const [showCurrentPassword, setShowCurrentPassword]     = useState(false);
  const [showNewPassword, setShowNewPassword]             = useState(false);
  const [showConfirmNewPassword, setShowConfirmNewPassword] = useState(false);

  // Validar requisitos de la nueva contraseña
  useEffect(() => {
    setLengthValid(password.length >= 8);
    setUpperValid(/[A-Z]/.test(password));
    setLowerValid(/[a-z]/.test(password));
    setNumberValid(/[0-9]/.test(password));
  }, [password]);

  // ─── Abrir modal de recorte de cabecera ────────────────────────────────
  const handleOpenImageModal = () => {
    openModal(
      <ModalImageHeader
        initialImageUrl={previewUrl || profData?.cabecera || ''}
        initialCrop={crop}
        initialZoom={zoom}
        initialCroppedAreaPixels={croppedAreaPixels}
        onCancel={() => closeModal(activeTab)}
        onConfirm={({ file, croppedArea, zoom: z, crop: c }) => {
          if (file) {
            setSelectedFile(file);
            setPreviewUrl(URL.createObjectURL(file));
          }
          setCrop(c); setZoom(z); setCroppedAreaPixels(croppedArea);
          closeModal(activeTab);
        }}
      />,
      {
        tab: activeTab,
        overlayClassName: 'pet-detail-overlay',
        disableSwipe: true,
        disableHeader: true
      }
    );
  };

  // ─── Handlers para logo ────────────────────────────────────────────────
  const logoInputRef = useRef(null);
  const handleLogoClick = () => logoInputRef.current?.click();
  const handleLogoChange = async e => {
    const file = e.target.files[0];
    if (!file) return;
    let jpegFile = file;
    if (file.type !== 'image/jpeg') {
      try {
        jpegFile = await convertFileToJpeg(file, 0.9);
      } catch {
        setErrorMsg('Sólo se permiten imágenes JPG.');
        return;
      }
    }
    setSelectedLogoFile(jpegFile);
    setPreviewLogoUrl(URL.createObjectURL(jpegFile));
    setLogoError(false);
  };

  // ─── Abrir modal ubicación ────────────────────────────────────────────
  const handleOpenLocationModal = () => {
    openModal(
      <ModalLocation
        initialLocation={{ ...location }}
        onAccept={newLoc => {
          setDireccion(newLoc.address);
          setLocation(newLoc);
          closeModal(activeTab);
        }}
        onCancel={() => closeModal(activeTab)}
        hideRange={true}
      />,
      {
        tab: activeTab,
        overlayClassName: 'pet-detail-overlay',
        disableSwipe: true,
        disableHeader: true
      }
    );
  };

  // ─── Guardar cambios en backend ───────────────────────────────────────
  const persistProfile = useCallback(async () => {
    const updates = {};

    // Logo → S3 y URL
    if (selectedLogoFile) {
      const logoFile = await convertFileToJpeg(selectedLogoFile, 0.9);
      const { data: presignLogo } = await API.post(
        '/profesionales/logo-upload-url/',
        { filename: logoFile.name, content_type: logoFile.type }
      );
      const formLogo = new FormData();
      Object.entries(presignLogo.fields).forEach(([k,v])=>formLogo.append(k,v));
      formLogo.append('file', logoFile);
      const resLogo = await fetch(presignLogo.url, { method:'POST', body:formLogo });
      if (!resLogo.ok) throw new Error('Logo upload failed');
      updates.logo = presignLogo.public_url;
    }

    // Cabecera → S3 y URL + crop
    if (selectedFile) {
      const headerFile = await convertFileToJpeg(selectedFile, 0.9);
      const { data: presign } = await API.post(
        '/profesionales/header-upload-url/',
        { filename: headerFile.name, content_type: headerFile.type }
      );
      const formData = new FormData();
      Object.entries(presign.fields).forEach(([k,v])=>formData.append(k,v));
      formData.append('file', headerFile);
      const res = await fetch(presign.url, { method:'POST', body:formData });
      if (!res.ok) throw new Error('Header upload failed');
      updates.cabecera = presign.public_url;
      updates.cabecera_crop = { crop, zoom, croppedAreaPixels };
    } else if (croppedAreaPixels) {
      updates.cabecera_crop = { crop, zoom, croppedAreaPixels };
    }

    // Nombre
    if (nombre && nombre !== initialNombre) {
      updates.nombre = nombre;
    }
    // Tipo
    if (tipo !== initialTipo) {
      updates.tipo = tipo;
    }
    // Teléfono (+34 prefix)
    if (telefono || initialTelefono) {
      const fullT = telefono ? `+34${telefono}` : '';
      if (fullT !== profData.telefono) updates.telefono = fullT;
    }
    // WhatsApp (+34 prefix)
    if (whatsapp || initialWhatsapp) {
      const fullW = whatsapp ? `+34${whatsapp}` : '';
      if (fullW !== profData.whatsapp) updates.whatsapp = fullW;
    }
    // Email
    if (email !== initialEmail && emailValid) {
      updates.email = email;
    }

    // Horario: sólo si ha cambiado respecto al inicial
    if (horario !== initialHorario) {
      updates.horario = horario;
    }

    // Dirección y coordenadas: usamos address / lat_write / lng_write
    // Sólo incluimos cada campo si realmente cambió
    if (location.address !== initialLocation.address) {
      updates.address = location.address;
    }
    if (location.lat !== initialLocation.lat) {
      updates.lat_write = location.lat;
    }
    if (location.lng !== initialLocation.lng) {
      updates.lng_write = location.lng;
    }

    // ─── Si estamos cambiando contraseña, validar y añadir al payload
    if (isChangingPassword) {
      if (!currentPassword || !password || !password2) {
        throw new Error('Debes rellenar los tres campos de contraseña.');
      }
      if (password !== password2) {
        throw new Error('La nueva contraseña y su confirmación no coinciden.');
      }
      if (!lengthValid || !upperValid || !lowerValid || !numberValid) {
        throw new Error('La contraseña no cumple los requisitos de seguridad.');
      }
      updates.current_password = currentPassword;
      updates.password = password;
    }

    // ─── Redes Sociales ───────────────────────────────────────────────
    if (enlace !== initialEnlace)     updates.enlace     = enlace;
    if (twitter !== initialTwitter)   updates.twitter    = twitter;
    if (facebook !== initialFacebook) updates.facebook   = facebook;
    if (instagram !== initialInstagram) updates.instagram = instagram;
    if (youtube !== initialYoutube)   updates.youtube    = youtube;

    // ─── Campos de colaboración ───────────────────────────────────────────────
    if (acoge !== initialAcoge)             updates.acoge        = acoge;
    if (apadrina !== initialApadrina)       updates.apadrina     = apadrina;
    if (voluntariado !== initialVoluntariado) updates.voluntariado = voluntariado;
    if (donaciones !== initialDonaciones)   updates.donaciones   = donaciones;
    if (socio !== initialSocio)             updates.socio        = socio;

    // ─── Sobre nosotros ───────────────────────────────────────────────
    if (aboutus !== initialAboutus) updates.aboutus = aboutus;

    if (Object.keys(updates).length) {
      await API.patch(`/profesionales/${professionalId}/`, updates);
      queryClient.invalidateQueries(['professional', professionalId]);
    }
  }, [
    selectedLogoFile,
    selectedFile,
    crop, zoom, croppedAreaPixels,
    nombre, initialNombre,
    tipo,   initialTipo,
    telefono, initialTelefono,
    whatsapp, initialWhatsapp,
    email, emailValid, initialEmail,
    horario, initialHorario,
    location, initialLocation,
    currentPassword, password, password2, isChangingPassword,
    lengthValid, upperValid, lowerValid, numberValid,
    enlace, initialEnlace,
    twitter, initialTwitter,
    facebook, initialFacebook,
    instagram, initialInstagram,
    youtube, initialYoutube,
    acoge, initialAcoge,
    apadrina, initialApadrina,
    voluntariado, initialVoluntariado,
    donaciones, initialDonaciones,
    socio, initialSocio,
    aboutus, initialAboutus,
    profData,
    professionalId,
    queryClient
  ]);

  // ─── Registrar acciones Guardar/Cancelar ─────────────────────────────
  useEffect(() => {
    if (!modalId) return;
    registerHeaderActions(modalId, {
      onSave: async () => {
        setSaving(true);
        setErrorMsg('');
        try {
          await persistProfile();
          closeModal(activeTab);
        } catch (err) {
          console.error(err);
          setErrorMsg(err.message || 'Error al guardar.');
          setSaving(false);
          return;
        }
      },
      onCancel: () => {
        // Resetear tódo a valores originales
        setSelectedFile(null);
        setSelectedLogoFile(null);
        setErrorMsg('');
        setCrop({ x: 0, y: 0 });
        setZoom(1);
        setCroppedAreaPixels(null);
        setPreviewUrl(blobHeaderUrl || profData?.cabecera || '');
        setPreviewLogoUrl(blobLogoUrl || profData?.logo || '');
        setLogoError(false);
        setNombre(profData.nombre || '');
        setTipo(profData.tipo || '');
        setTelefono((profData.telefono||'').replace(/^\+34/, ''));
        setWhatsapp((profData.whatsapp||'').replace(/^\+34/, ''));
        setEmail(profData.email || '');
        setEmailValid(true);
        setHorario(profData.horario || '');
        setDireccion(profData.direccion || '');  // ← reset dirección
        setDireccion(initialLocation.address);
        setLocation(initialLocation);
        setSaving(false);
        setEnlace(initialEnlace);
        setTwitter(initialTwitter);
        setFacebook(initialFacebook);
        setInstagram(initialInstagram);
        setYoutube(initialYoutube);
        setAcoge(initialAcoge);
        setApadrina(initialApadrina);
        setVoluntariado(initialVoluntariado);
        setDonaciones(initialDonaciones);
        setSocio(initialSocio);
        setAboutus(initialAboutus);
        closeModal(activeTab);
      }
    });
  }, [
    modalId,
    persistProfile,
    registerHeaderActions,
    closeModal,
    activeTab,
    initialLocation,
    profData,
    blobHeaderUrl,
    blobLogoUrl,
    initialEnlace, initialTwitter, initialFacebook, initialInstagram, initialYoutube,
    initialAcoge, initialApadrina, initialVoluntariado, initialDonaciones, initialSocio,
    initialAboutus,
  ]);

  // ─── Render ───────────────────────────────────────────────────────────
  if (isLoading) return
    <div className="modalProfOverlay">
      <div className="modal-spinner-overlay">
        <Spinner
          size={222}
          spinnerText="Cargando"
          showEllipses
          pulseScale={1.008}
          pulseDuration={1}
          textCycleDuration={1.6}
          textColor="black"
          textSize="0.7rem"
          textOffsetX="-12px"
          textOffsetY="20px"
        />
      </div>
    </div>;
  if (isError || !profData) return <div className="modalProfOverlay"><p>Error al cargar datos.</p></div>;

  return (
    <div className="modalProfOverlay">
      {saving && (
        <div className="modal-spinner-overlay">
          <Spinner
            size={222}
            spinnerText="Guardando"
            showEllipses
            pulseScale={1.008}
            pulseDuration={1}
            textCycleDuration={1.6}
            textColor="black"
            textSize="0.7rem"
            textOffsetX="-12px"
            textOffsetY="20px"
          />
        </div>
      )}
      <div className="modalProfCard" style={{ pointerEvents: saving ? 'none' : 'auto' }}>
        {/* Cabecera editable */}
        <div
          className="modalProProfilefHeaderImageEdit clickable"
          ref={containerRef}
          onClick={handleOpenImageModal}
        >
          <img
            ref={imgRef}
            src={previewUrl}
            alt="Cabecera profesional"
            onLoad={handleImgLoad}
            style={imgStyle}
          />
        </div>

        {/* Divisor */}
        <div className="modalProfHeaderDivider" />

        {/* Logo + Nombre */}
        <div className="modalProLogoSection">
          <div className="modalProLogoContainer clickable" onClick={handleLogoClick}>
            {!logoError && previewLogoUrl ? (
              <img
                src={previewLogoUrl}
                alt="Logo profesional"
                className="modalProLogo"
                onError={() => setLogoError(true)}
              />
            ) : (
              <div className="modalProLogoPlaceholder" />
            )}
            <input
              type="file"
              accept="image/*"
              ref={logoInputRef}
              style={{ display: 'none' }}
              onChange={handleLogoChange}
            />
          </div>
          <div className="modalProNombreCell">
            <label className="nombre-label">
              <span className="char-count">{nombre.length}/20</span>
              <input
                type="text"
                className="nombre-input"
                value={nombre}
                maxLength={20}
                onChange={e => setNombre(e.target.value)}
                placeholder="Nombre profesional"
              />
            </label>
          </div>
        </div>

        {/* Divisor */}
        <div className="modalProfHeaderDivider" />

        {/* Campos adicionales: Tipo, Teléfono, WhatsApp, Email */}
        <div className="modalProEditForm">
          {/* Tipo */}
          <div className="form-group">
            <label>Tipo</label>
            <input
              type="text"
              value={tipo}
              onChange={e => setTipo(e.target.value)}
              placeholder="Ej: ONG, Protectora…"
            />
          </div>
          {/* Teléfono */}
          <div className="form-group phone-group">
            <label>Teléfono</label>
            <div className="phone-wrapper">
              <span className="phone-prefix">+34</span>
              <input
                type="tel"
                className="phone-input"
                value={telefono}
                onChange={e => {
                  const d = e.target.value.replace(/\D/g, '').slice(0, 9);
                  setTelefono(d);
                }}
                placeholder="6XX XXX XXX"
              />
            </div>
          </div>
          {/* WhatsApp */}
          <div className="form-group phone-group">
            <label>WhatsApp</label>
            <div className="phone-wrapper">
              <span className="phone-prefix">+34</span>
              <input
                type="tel"
                className="phone-input"
                value={whatsapp}
                onChange={e => {
                  const d = e.target.value.replace(/\D/g, '').slice(0, 9);
                  setWhatsapp(d);
                }}
                placeholder="6XX XXX XXX"
              />
            </div>
          </div>
          {/* Email */}
          <div className="form-group">
            <label>Email</label>
            <input
              type="email"
              value={email}
              onChange={e => {
                const v = e.target.value;
                setEmail(v);
                const ok = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
                setEmailValid(ok);
                setEmailMessage(ok ? '' : 'Formato inválido');
              }}
              placeholder="email@dominio.com"
            />
            {emailValid === false && (
              <small className="invalid">{emailMessage}</small>
            )}
          </div>
        </div>

        {/* Campo Horario */}
        <div className="modalProEditForm">
          <div className="form-group">
            <label>Horario</label>
            <textarea
              ref={horarioRef}
              className="horario-textarea"
              value={horario}
              onChange={e => setHorario(e.target.value)}
              placeholder="Indica tu horario"
              rows={1}
            />
          </div>
        </div>

        {/* Dirección y botones */}
        <div className="modalProEditForm">
          <div className="form-group">
            <label>Dirección</label>
            <input
              type="text"
              value={direccion}
              readOnly
              placeholder="Ciudad / Población"
            />
          </div>
          <div className="buttons-actions">
            <button
              type="button"
              className="link-change-buttons"
              onClick={handleOpenLocationModal}
            >
              Actualizar dirección
            </button>
            <button
              type="button"
              className="link-change-buttons"
              onClick={() => setIsChangingPassword(prev => !prev)}
            >
              Cambiar contraseña
            </button>
          </div>
        </div>

        {errorMsg && <div className="error-msg">{errorMsg}</div>}

        {/* ─── Campos de contraseña ──────────────────────────────────────── */}
        {isChangingPassword && (
          <>
            <div className="form-group relative">
              <label>
                Contraseña actual <span className="required">*</span>
              </label>
              <input
                type={showCurrentPassword ? 'text' : 'password'}
                value={currentPassword}
                onChange={e => setCurrentPassword(e.target.value)}
              />
              <button
                type="button"
                className="toggle-btn"
                onClick={() => setShowCurrentPassword(prev => !prev)}
              >
                {showCurrentPassword ? '🙈' : '👁️'}
              </button>
            </div>
            <div className="form-group relative">
              <label>
                Nueva contraseña <span className="required">*</span>
              </label>
              <input
                type={showNewPassword ? 'text' : 'password'}
                value={password}
                onChange={e => setPassword(e.target.value)}
              />
              <button
                type="button"
                className="toggle-btn"
                onClick={() => setShowNewPassword(prev => !prev)}
              >
                {showNewPassword ? '🙈' : '👁️'}
              </button>
            </div>
            <div className="form-group relative">
              <label>
                Confirmar nueva contraseña <span className="required">*</span>
              </label>
              <input
                type={showConfirmNewPassword ? 'text' : 'password'}
                value={password2}
                onChange={e => setPassword2(e.target.value)}
              />
              <button
                type="button"
                className="toggle-btn"
                onClick={() => setShowConfirmNewPassword(prev => !prev)}
              >
                {showConfirmNewPassword ? '🙈' : '👁️'}
              </button>
            </div>
            <ul className="password-requirements">
              <li className={lengthValid ? 'valid' : 'invalid'}>Al menos 8 caracteres</li>
              <li className={upperValid ? 'valid' : 'invalid'}>Una mayúscula</li>
              <li className={lowerValid ? 'valid' : 'invalid'}>Una minúscula</li>
              <li className={numberValid ? 'valid' : 'invalid'}>Un número</li>
            </ul>
          </>
        )}

        {/* Divisor */}
        <div className="modalProfHeaderDivider" />

        {/* ─── Redes Sociales ─────────────────────────────────────────────── */}
        <div className="modalProEditForm">
          {[
            { label: 'Visita nuestra web',      value: enlace,    setter: setEnlace,    placeholder: 'https://...' },
            { label: 'Twitter',         value: twitter,   setter: setTwitter,   placeholder: 'Inserta tu enlace' },
            { label: 'Facebook',        value: facebook,  setter: setFacebook,  placeholder: 'Inserta tu enlace' },
            { label: 'Instagram',       value: instagram, setter: setInstagram, placeholder: 'Inserta tu enlace' },
            { label: 'YouTube',         value: youtube,   setter: setYoutube,   placeholder: 'Inserta tu enlace' },
          ].map(({ label, value, setter, placeholder }) => (
            <div key={label} className="autocomplete-input-container">
              <label>{label}</label>
              <input
                type="text"
                value={value}
                placeholder={placeholder}
                onChange={e => setter(e.target.value)}
              />
              {value && (
                <button
                  type="button"
                  className="clear-btn"
                  onClick={() => setter('')}
                  aria-label={`Limpiar ${label}`}
                >
                  ×
                </button>
              )}
            </div>
          ))}
        </div>

        {/* Divisor */}
        <div className="modalProfHeaderDivider" />

        {/* ─── Campos de colaboración ───────────────────────────────────────── */}
        <div className="modalProEditForm">
          {[
            { label: 'Acoge',        value: acoge,        setter: setAcoge,        placeholder: 'Inserta tu enlace' },
            { label: 'Apadrina',     value: apadrina,     setter: setApadrina,     placeholder: 'Inserta tu enlace' },
            { label: 'Voluntariado', value: voluntariado, setter: setVoluntariado, placeholder: 'Inserta tu enlace' },
            { label: 'Donaciones',   value: donaciones,   setter: setDonaciones,   placeholder: 'Inserta tu enlace' },
            { label: 'Socio',        value: socio,        setter: setSocio,        placeholder: 'Inserta tu enlace' },
          ].map(({ label, value, setter, placeholder }) => (
            <div key={label} className="autocomplete-input-container">
              <label>{label}</label>
              <input
                type="text"
                value={value}
                placeholder={placeholder}
                onChange={e => setter(e.target.value)}
              />
              {value && (
                <button
                  type="button"
                  className="clear-btn"
                  onClick={() => setter('')}
                  aria-label={`Limpiar ${label}`}
                >
                  ×
                </button>
              )}
            </div>
          ))}
        </div>

        {/* Divisor */}
        <div className="modalProfHeaderDivider" />

        {/* Campo Sobre nosotros */}
        <div className="modalProEditForm">
          <div className="form-group">
            <label>Sobre nosotros</label>
            <textarea
              ref={aboutusRef}
              className="aboutus-textarea"
              value={aboutus}
              onChange={e => setAboutus(e.target.value)}
              placeholder="Cuéntanos quiénes sois"
              rows={1}
            />
          </div>
        </div>

      {errorMsg && <p className="error-msg">{errorMsg}</p>}
      </div>
    </div>
  );
}
